import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import "../styles/MapComponent.css";
import { useDataContext } from "../../contexts/DataContext";
import { useUserContext } from "../../contexts/UserContext";
import { getReportColor } from "../../utils/reportProps";
import { ReportData } from "../../types/global";
import { useMarkerIcon } from "../hooks/useMapMarkers";
import {
  useReportClusters,
  isClusterFeature,
} from "./useReportClusters";
import { getClusterBorderColors } from "./mapClustering";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { loginUser, signupUser, linkTelegram, AuthResponse } from "../../api/auth";
import { startVkAuth } from "../../platform/social/vk";
import { getInitData, IS_TELEGRAM } from "../../platform";
import { useBridgeAuth } from "../auth/useBridgeAuth";
import BridgeWaiting from "../auth/BridgeWaiting";
import LoginButton from "../auth/LoginButton";
import AuthPopup from "../auth/AuthPopup";
import UserPanel from "../user/UserPanel";
import AdminPanel from "../admin/AdminPanel";
import MapLoadingState from "./MapLoadingState";
import MapErrorState from "./MapErrorState";

// import MapReportsStatus from "./MapReportsStatus";
import {
  GOOGLE_MAPS_API_KEY,
  DEFAULT_LAT,
  DEFAULT_LNG,
  DEFAULT_ZOOM,
  MAP_CONFIG,
  CLUSTER_CONFIG,
  UserStatus,
} from "../../constants";
import LanguageSwitcher from "../LanguageSwitcher";

// Marker stacking + dimming, mirrors the mobile app's z-index hierarchy.
const SELECTED_MARKER_Z_INDEX = 1000;
const CLUSTER_MARKER_Z_INDEX = 20;
const REPORT_MARKER_Z_INDEX = 10;
const DIMMED_MARKER_OPACITY = 0.62;

interface MapComponentProps {
  reports?: ReportData[];
  center?: { lat: number; lng: number };
  zoom?: number;
  selectedReportId?: string | null;
  onReportClick?: (report: ReportData) => void;
  // Called when a non-expandable cluster is tapped: opens a multi-report
  // preview over all the cluster's report ids.
  onClusterReportsSelect?: (reportIds: string[]) => void;
  onOpenChatsList?: () => void;
  chatsListBadgeCount?: number;
}

const MapComponent: React.FC<MapComponentProps> = ({
  reports: propReports,
  center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
  zoom = DEFAULT_ZOOM,
  selectedReportId = null,
  onReportClick,
  onClusterReportsSelect,
  onOpenChatsList,
  chatsListBadgeCount = 0,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [markersCreated, setMarkersCreated] = useState(false);
  const [advancedMarkerLib, setAdvancedMarkerLib] = useState<any>(null);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Pool of live AdvancedMarkerElements, keyed by feature id, diffed per
  // viewport recompute so we never tear down and rebuild the whole set.
  const markerPoolRef = useRef<Map<string, any>>(new Map());
  const didFitBoundsRef = useRef(false);

  // Get reports data from context or props
  const {
    reports: contextReports,
    // loading: reportsLoading,
    // error: reportsError,
  } = useDataContext();

  // Use prop reports if provided, otherwise use context reports
  const reports = propReports || contextReports;

  // Get user context
  const { user, setUser, isLoggedIn, logout } = useUserContext();
  const isAdmin = user?.status === UserStatus.admin;

  // Authentication handlers
  const handleLoginClick = useCallback(() => {
    if (isLoggedIn) {
      setShowUserPanel(true);
    } else {
      setShowAuthPopup(true);
    }
  }, [isLoggedIn]);

  const handleUserPanelClose = useCallback(() => {
    setShowUserPanel(false);
  }, []);

  const handleAdminPanelOpen = useCallback(() => {
    setShowAdminPanel(true);
  }, []);

  const handleAdminPanelClose = useCallback(() => {
    setShowAdminPanel(false);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setShowUserPanel(false);
    setShowAdminPanel(false);
  }, [logout]);

  const handleUserUpdate = useCallback(
    (updatedUser: any) => {
      setUser(updatedUser);
    },
    [setUser]
  );

  // After any successful auth: inside Telegram, auto-link this Telegram identity
  // (best-effort — a link failure still leaves the user logged in), then set the
  // session and open the profile.
  const finishAuth = useCallback(
    async (response: AuthResponse) => {
      let account = response.user;
      if (IS_TELEGRAM) {
        try {
          const linked = await linkTelegram(getInitData());
          account = linked.user;
        } catch (error) {
          console.error("Telegram auto-link failed (proceeding logged in):", error);
        }
      }
      setUser(account);
      setShowAuthPopup(false);
      setShowUserPanel(true);
    },
    [setUser]
  );

  const bridge = useBridgeAuth({ onSession: finishAuth });

  const handleLogin = useCallback(
    async (emailOrNickname: string, password: string) => {
      try {
        const response = await loginUser({ emailOrNickname, password });
        await finishAuth(response);
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },
    [finishAuth]
  );

  const handleVkLogin = useCallback(() => {
    startVkAuth({ mode: "login" }).catch((error) => {
      console.error("VK login failed to start:", error);
    });
  }, []);

  const handleSignup = useCallback(
    async (
      nickname: string,
      email: string,
      password: string,
      subscribed: boolean,
      phone: string,
      phoneVerificationToken: string,
      fullname: string
    ) => {
      try {
        const response = await signupUser({
          nickname,
          email,
          password,
          subscribed,
          phone,
          phoneVerificationToken,
          fullname,
        });
        await finishAuth(response);
      } catch (error) {
        console.error("Signup error:", error);
        throw error;
      }
    },
    [finishAuth]
  );

  const handleAuthPopupClose = useCallback(() => {
    setShowAuthPopup(false);
  }, []);

  // Custom hooks for marker functionality
  const { createMarkerElement, createClusterElement } = useMarkerIcon();

  // Memoize center and zoom to prevent unnecessary map updates
  const memoizedCenter = useMemo(() => center, [center]);
  const memoizedZoom = useMemo(() => zoom, [zoom]);

  // Index reports by id for O(1) lookups during clustering/selection.
  const reportById = useMemo(() => {
    const map = new Map<string, ReportData>();
    reports.forEach((report) => map.set(report._id, report));
    return map;
  }, [reports]);

  // Keep a ref copy so the select-pan effect can read the latest coordinates
  // without re-running on every status overlay.
  const reportByIdRef = useRef(reportById);
  reportByIdRef.current = reportById;

  // Supercluster index + the cluster/point features for the current viewport.
  const { index, clusters } = useReportClusters(map, reports);

  // Google Maps loader
  useGoogleMapsLoader({
    apiKey: GOOGLE_MAPS_API_KEY,
    onLoad: async () => {
      try {
        // Give the API a moment to fully initialize
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Import the marker library
        const markerLib = (await google.maps.importLibrary("marker")) as any;
        setAdvancedMarkerLib(markerLib);
        setIsLoaded(true);
      } catch (error) {
        console.error("Failed to load marker library:", error);
        setError("Failed to load marker library");
      }
    },
    onError: setError,
  });

  // Initialize map when Google Maps API is loaded
  useEffect(() => {
    if (
      isLoaded &&
      mapRef.current &&
      !map &&
      window.google?.maps?.ControlPosition
    ) {
      const newMap = new google.maps.Map(mapRef.current, {
        center: memoizedCenter,
        zoom: memoizedZoom,
        minZoom: MAP_CONFIG.MIN_ZOOM, // floor so the map always fills the viewport (no grey band)
        mapId: MAP_CONFIG.MAP_ID, // Required for Advanced Markers
        // Note: Custom styles are not supported when mapId is present
        // Styles must be configured in Google Cloud Console
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        scaleControl: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
        gestureHandling: "auto",
      });
      setMap(newMap);
    }
  }, [isLoaded, memoizedCenter, memoizedZoom, map]);

  // Reconcile markers with the current viewport clusters/points. The visible
  // feature count is bounded (clustering + viewport culling), so we diff a
  // marker pool by key instead of tearing the whole set down each move.
  useEffect(() => {
    if (!map || !advancedMarkerLib) return;
    const AdvancedMarkerElement = advancedMarkerLib.AdvancedMarkerElement;
    const pool = markerPoolRef.current;
    const nextKeys = new Set<string>();

    const upsert = (
      key: string,
      position: { lat: number; lng: number },
      signature: string,
      buildContent: () => HTMLElement,
      zIndex: number,
      onClick: () => void
    ) => {
      let marker = pool.get(key);
      if (!marker) {
        marker = new AdvancedMarkerElement({
          map,
          position,
          content: buildContent(),
          zIndex,
        });
        marker.addListener("click", () => marker.__onClick?.());
        marker.__signature = signature;
        pool.set(key, marker);
      } else {
        marker.position = position;
        marker.zIndex = zIndex;
        if (marker.__signature !== signature) {
          marker.content = buildContent();
          marker.__signature = signature;
        }
      }
      marker.__onClick = onClick;
      nextKeys.add(key);
    };

    clusters.forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const position = { lat, lng };

      if (isClusterFeature(feature)) {
        const clusterId = feature.properties.cluster_id;
        const count = feature.properties.point_count;
        const { color, secondaryColor } = getClusterBorderColors(
          feature.properties
        );
        upsert(
          `cluster-${clusterId}`,
          position,
          `cluster:${count}:${color}:${secondaryColor ?? ""}`,
          () => createClusterElement(count, color, secondaryColor),
          CLUSTER_MARKER_Z_INDEX,
          () => {
            const expansionZoom = index.getClusterExpansionZoom(clusterId);
            const currentZoom = map.getZoom() ?? 0;
            // Only zoom when the cluster actually separates within our zoom
            // range. supercluster returns an expansion zoom ABOVE maxZoom for
            // points too close to ever split — in that case (or when already
            // zoomed in enough) fall through to the multi-report preview
            // instead of fruitlessly zooming to the capped max.
            if (
              expansionZoom > currentZoom &&
              expansionZoom <= CLUSTER_CONFIG.MAX_ZOOM
            ) {
              map.panTo(position);
              map.setZoom(expansionZoom);
              return;
            }
            // Can't separate further — open a multi-report preview over every
            // report in the cluster so each one is reachable.
            const leafIds = index
              .getLeaves(clusterId, Infinity)
              .map((leaf) => leaf.properties.reportId)
              .filter((id) => reportById.has(id));
            if (!leafIds.length) return;
            map.panTo(position);
            if (onClusterReportsSelect) {
              onClusterReportsSelect(leafIds);
            } else {
              const first = reportById.get(leafIds[0]);
              if (first) onReportClick?.(first);
            }
          }
        );
        return;
      }

      const reportId = feature.properties.reportId;
      if (reportId === selectedReportId) return; // drawn on top separately
      const report = reportById.get(reportId);
      if (!report) return;
      const dimmed = Boolean(selectedReportId);
      upsert(
        `report-${reportId}`,
        position,
        `report:${getReportColor(report)}:${dimmed ? 1 : 0}`,
        () => {
          const el = createMarkerElement(report, false);
          if (dimmed) el.style.opacity = String(DIMMED_MARKER_OPACITY);
          return el;
        },
        REPORT_MARKER_Z_INDEX,
        () => {
          map.panTo(position);
          onReportClick?.(report);
        }
      );
    });

    // Selected report is always drawn on top, even when it would otherwise be
    // folded into a cluster at the current zoom.
    if (selectedReportId) {
      const selectedReport = reportById.get(selectedReportId);
      if (
        selectedReport &&
        typeof selectedReport.geoposition?.latitude === "number" &&
        typeof selectedReport.geoposition?.longitude === "number"
      ) {
        const position = {
          lat: selectedReport.geoposition.latitude,
          lng: selectedReport.geoposition.longitude,
        };
        upsert(
          `selected-${selectedReportId}`,
          position,
          `selected:${getReportColor(selectedReport)}`,
          () => createMarkerElement(selectedReport, true),
          SELECTED_MARKER_Z_INDEX,
          () => onReportClick?.(selectedReport)
        );
      }
    }

    // Remove markers that are no longer visible.
    pool.forEach((marker, key) => {
      if (!nextKeys.has(key)) {
        marker.map = null;
        pool.delete(key);
      }
    });
  }, [
    map,
    advancedMarkerLib,
    clusters,
    index,
    reportById,
    selectedReportId,
    createMarkerElement,
    createClusterElement,
    onReportClick,
    onClusterReportsSelect,
  ]);

  // Pan to the selected report (only when the selection itself changes).
  useEffect(() => {
    if (!map || !selectedReportId) return;
    const report = reportByIdRef.current.get(selectedReportId);
    if (
      !report ||
      typeof report.geoposition?.latitude !== "number" ||
      typeof report.geoposition?.longitude !== "number"
    ) {
      return;
    }
    map.panTo({
      lat: report.geoposition.latitude,
      lng: report.geoposition.longitude,
    });
    const currentZoom = map.getZoom();
    if (currentZoom && currentZoom < MAP_CONFIG.MIN_ZOOM_ON_SELECT) {
      map.setZoom(MAP_CONFIG.MIN_ZOOM_ON_SELECT);
    }
  }, [map, selectedReportId]);

  // Fit bounds to all reports once, the first time they arrive.
  useEffect(() => {
    if (!map || didFitBoundsRef.current || reports.length === 0) return;

    if (reports.length === 1) {
      const { latitude, longitude } = reports[0].geoposition;
      map.setCenter({ lat: latitude, lng: longitude });
      didFitBoundsRef.current = true;
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    reports.forEach((report) => {
      bounds.extend(
        new google.maps.LatLng(
          report.geoposition.latitude,
          report.geoposition.longitude
        )
      );
    });
    map.fitBounds(bounds);
    didFitBoundsRef.current = true;

    const listener = google.maps.event.addListener(map, "bounds_changed", () => {
      if (map.getZoom() && map.getZoom()! > MAP_CONFIG.MAX_ZOOM_ON_FIT_BOUNDS) {
        map.setZoom(MAP_CONFIG.MAX_ZOOM_ON_FIT_BOUNDS);
      }
      google.maps.event.removeListener(listener);
    });
  }, [map, reports]);

  // Clear the marker pool on unmount.
  useEffect(() => {
    const pool = markerPoolRef.current;
    return () => {
      pool.forEach((marker) => {
        marker.map = null;
      });
      pool.clear();
    };
  }, []);

  // Early returns for different states
  if (error) {
    return <MapErrorState error={error} />;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return <MapErrorState error={"Maps configuration needed"} />;
  }

  return (
    <div className="map-container">
      {/* Login / account button. In Telegram, auth is automatic — but this
          button is still the only entry to the profile panel (logout, link an
          existing account) and the password fallback if auto-auth failed, so
          it must always render. */}
      <LoginButton
        onClick={handleLoginClick}
        isLoggedIn={isLoggedIn}
        userNickname={user?.nickname}
        userAvatar={user?.avatar}
      />
      {isLoggedIn && isAdmin && onOpenChatsList && (
        <button
          type="button"
          className="map-chat-button"
          onClick={onOpenChatsList}
          title={t("chat.chats")}
          aria-label={t("chat.chats")}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/send.png`}
            alt=""
            aria-hidden="true"
          />
          {chatsListBadgeCount > 0 && (
            <span className="map-chat-badge">
              {chatsListBadgeCount > 99 ? "99+" : chatsListBadgeCount}
            </span>
          )}
        </button>
      )}
      {isLoggedIn && isAdmin && (
        <button
          type="button"
          className="map-admin-button"
          onClick={handleAdminPanelOpen}
          title={t("admin.exports.open")}
          aria-label={t("admin.exports.open")}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3l7 3v5c0 4.5-2.9 8.6-7 10-4.1-1.4-7-5.5-7-10V6l7-3z" />
            <path d="M9 12l2 2 4-5" />
          </svg>
        </button>
      )}
      <LanguageSwitcher />

      {/* Authentication Popup */}
      <AuthPopup
        isOpen={showAuthPopup}
        onClose={handleAuthPopupClose}
        onLogin={handleLogin}
        onSignup={handleSignup}
        onVk={IS_TELEGRAM ? undefined : handleVkLogin}
        onBridge={IS_TELEGRAM ? bridge.start : undefined}
      />

      {bridge.pending && (
        <BridgeWaiting provider={bridge.pending} onCancel={bridge.cancel} />
      )}
      {bridge.error === "timeout" && (
        <div className="auth-error" role="alert">{t("auth.bridgeTimeout")}</div>
      )}

      {/* User Panel */}
      <UserPanel
        user={showUserPanel ? user : null}
        onClose={handleUserPanelClose}
        onLogout={handleLogout}
        onUserUpdate={handleUserUpdate}
      />

      {showAdminPanel && isAdmin && (
        <AdminPanel onClose={handleAdminPanelClose} />
      )}

      {/* <MapReportsStatus
        reportsLoading={reportsLoading}
        reportsError={reportsError}
        reportsCount={memoizedReports.length}
        markersCount={markersRef.current.length}
        markersCreated={markersCreated}
      /> */}

      <MapLoadingState isLoaded={isLoaded} />

      <div
        ref={mapRef}
        className={`map ${isLoaded ? "map-loaded" : "map-loading"}`}
      />

    </div>
  );
};

export default MapComponent;
